import { memo, useState, useCallback } from 'react';
import { Play, Pause, SkipBack, SkipForward, ChevronDown, ChevronUp, Music } from 'lucide-react';
import { useAudio } from '@/context/AudioContext';

const STORAGE_KEY = 'mini-player-collapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function formatTime(seconds: number): string {
  if (!seconds || !isFinite(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function MiniPlayerInner() {
  const {
    currentTrack,
    isPlaying,
    isLoading,
    currentTime,
    duration,
    pause,
    resume,
    playNext,
    playPrevious,
    hasNext,
    hasPrevious,
  } = useAudio();

  const [collapsed, setCollapsed] = useState(readCollapsed);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
      } catch {
        /* noop */
      }
      return next;
    });
  }, []);

  if (!currentTrack) return null;

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (collapsed) {
    return (
      <div className="bg-white shrink-0 border-t border-[#f0d2f0]/50">
        <button
          onClick={toggleCollapsed}
          className="w-full flex items-center gap-2 px-4 py-1.5 text-[#8e4585] hover:bg-[#f8e8f8] transition-colors"
        >
          <Music size={12} />
          <span className="text-[10px] font-medium truncate flex-1 text-left">
            {currentTrack.title || 'Now Playing'}
          </span>
          <ChevronUp size={12} />
        </button>
        <div className="h-[2px] bg-[#e5bce5]/20">
          <div className="h-full bg-[#8e4585] transition-all" style={{ width: `${progress}%` }} />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shrink-0 border-t border-[#f0d2f0]/50">
      <div className="mx-3 my-2 rounded-xl bg-[#63345e] px-3 py-2">
        <div className="flex items-center gap-2.5">
          {currentTrack.thumbnailUrl ? (
            <img
              src={currentTrack.thumbnailUrl}
              alt=""
              className="w-9 h-9 rounded-lg object-cover shrink-0"
            />
          ) : (
            <div className="w-9 h-9 rounded-lg bg-[#8e4585]/50 shrink-0" />
          )}

          <div className="flex-1 min-w-0">
            <p className="text-white text-[11px] font-semibold truncate">
              {currentTrack.title || 'Untitled'}
            </p>
            <p className="text-[#f0d2f0] text-[9px] opacity-70">
              {formatTime(currentTime)} / {formatTime(duration)}
            </p>
          </div>

          <div className="flex items-center gap-0.5 shrink-0">
            <button
              onClick={playPrevious}
              disabled={!hasPrevious}
              className="p-1.5 text-white/70 hover:text-white disabled:opacity-30 transition-colors"
              aria-label="Previous track"
            >
              <SkipBack size={14} />
            </button>
            <button
              onClick={isPlaying ? pause : resume}
              disabled={isLoading}
              className="p-1.5 bg-white/20 rounded-full text-white hover:bg-white/30 transition-colors"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
            </button>
            <button
              onClick={playNext}
              disabled={!hasNext}
              className="p-1.5 text-white/70 hover:text-white disabled:opacity-30 transition-colors"
              aria-label="Next track"
            >
              <SkipForward size={14} />
            </button>
          </div>

          <button
            onClick={toggleCollapsed}
            className="p-1 text-white/50 hover:text-white transition-colors shrink-0"
            aria-label="Collapse player"
          >
            <ChevronDown size={14} />
          </button>
        </div>

        <div className="mt-1.5 h-[3px] bg-[#e5bce5]/30 rounded-full overflow-hidden">
          <div
            className="h-full bg-[#e5bce5] rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
}

export const MiniPlayer = memo(MiniPlayerInner);
